import { createContext, useCallback, useContext, useRef, useState } from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Typography } from "@mui/material";
import { CloseRounded, WarningAmberRounded } from "@mui/icons-material";
import { fontSize, neutral } from "app/theme/tokens";
import { AppButton } from "app/components/ui/form";

/**
 * Standard dialog: title bar with close button, scrollable content, footer actions.
 * `loading` shows a thin progress bar under the title.
 */
export function AppDialog({
  open,
  onClose,
  title,
  subtitle,
  actions,
  children,
  maxWidth = "sm",
  fullWidth = true,
  loading = false,
  disableClose = false,
  contentSx,
  ...props
}) {
  const handleClose = (e, reason) => {
    if (disableClose || loading) return;
    onClose?.(e, reason);
  };
  return (
    <Dialog open={open} onClose={handleClose} maxWidth={maxWidth} fullWidth={fullWidth} {...props}>
      {title && (
        <DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: 1, pr: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box component="span" sx={{ display: "block" }}>{title}</Box>
            {subtitle && (
              <Typography sx={{ fontSize: fontSize.sm, color: neutral[500], fontWeight: 400 }}>{subtitle}</Typography>
            )}
          </Box>
          {onClose && !disableClose && (
            <IconButton size="small" onClick={(e) => handleClose(e, "closeButton")} aria-label="Close" disabled={loading}>
              <CloseRounded fontSize="small" />
            </IconButton>
          )}
        </DialogTitle>
      )}
      {loading && <LinearProgress />}
      <DialogContent sx={contentSx}>{children}</DialogContent>
      {actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  );
}

/** Controlled yes/no dialog. `onConfirm` may be async — the button shows a spinner meanwhile. */
export function ConfirmDialog({
  open,
  title = "Are you sure?",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  tone = "primary", // "primary" | "error" | "warning"
  onConfirm,
  onCancel,
}) {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm?.();
    } finally {
      setBusy(false);
    }
  };
  return (
    <AppDialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      disableClose={busy}
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {tone !== "primary" && <WarningAmberRounded sx={{ color: `${tone}.main`, fontSize: 20 }} />}
          {title}
        </Box>
      }
      actions={
        <>
          <AppButton variant="text" color="inherit" onClick={onCancel} disabled={busy}>
            {cancelText}
          </AppButton>
          <AppButton color={tone} onClick={confirm} loading={busy}>
            {confirmText}
          </AppButton>
        </>
      }
    >
      {typeof message === "string" ? <Typography sx={{ fontSize: fontSize.md, color: neutral[700] }}>{message}</Typography> : message}
    </AppDialog>
  );
}

const ConfirmContext = createContext(null);

/** Mount once near the root; enables `const confirm = useConfirm()`. */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolver = useRef();

  const confirm = useCallback(
    (options = {}) =>
      new Promise((resolve) => {
        resolver.current = resolve;
        setState(options);
      }),
    []
  );

  const finish = (result) => {
    resolver.current?.(result);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog open={Boolean(state)} {...state} onConfirm={() => finish(true)} onCancel={() => finish(false)} />
    </ConfirmContext.Provider>
  );
}

/**
 * Promise-based confirmation:
 *   if (await confirm({ title: "Delete job?", message: "…", tone: "error", confirmText: "Delete" })) { … }
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}
